import './types'

import { HtmlVersion } from '~/notes/@types'
import {
  ENABLE_AI_NOTE_PARAM,
  ENABLE_DEBUG_TRACKING_PARAM,
  HARDCODED_NOTE_PARAM,
  HARDCODED_NOTE_TYPE_PARAM,
} from '~/notes/constants'
import { HardcodedNoteType } from '~/notes/services/useInitialNoteLoader/hardCodedResponses'
import { getUserReadOnly } from '~/notes/utils/cookies'
import { getLocalStorageItem } from '~/notes/utils/webStorage'

import { isDev } from './getEnv'
import { getFlagValue } from './getFlagValue'
import { isFlagEnabledForUser } from './isFlagEnabled'

/**
 * Whether the application allows querying.
 */
export const isQueryEnabled = () =>
  isFlagEnabledForUser('QUERY_ENABLED', { defaultValue: isDev })

export const isReview = () =>
  new URLSearchParams(window.location.search).get('review') !== null

// If this is true, the following things will NOT occur:
//   - tracking analytics
//   - persisting draft notes
//   - persisting bullets
export const getIsNoPersistence = () =>
  isReview() ||
  // || isSalesDemoMode
  getUserReadOnly()

// Cypress

export const isCypress = !!window.Cypress
export const cyCachePatient: 'true' | '' =
  isCypress && getLocalStorageItem('cy-cache-patient') === 'true' ? 'true' : ''
export const cyCacheKey: string =
  (isCypress && getLocalStorageItem('cy-patient-id')) || ''

// Disables the in-app auth redirect when injecting auth token manually
// in the vite dev proxy with the PROXY_COOKIE environment variable.
// Turn this on for the user and site being tested.
export const getIsAuthRedirectDisabled = () =>
  isFlagEnabledForUser('frontend_disable_auth_redirect')

export const isDebugPerformance = isFlagEnabledForUser('DEBUG_PERFORMANCE')
export const isDebugTracking = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const debugTracking = urlParams.get(ENABLE_DEBUG_TRACKING_PARAM)
  if (debugTracking === 'true') {
    return true
  }

  return isFlagEnabledForUser('DEBUG_TRACKING')
}

// Sales Demo Mode - used to gate demo-only UI features like the Reset Demo button
export const isSalesDemoMode = () => isFlagEnabledForUser('SALES_DEMO_MODE')

export const getCurrentHtmlVersion = () => HtmlVersion.HtmlWithEMU

export const useNumbersInsteadOfHashtagsWhenAppendingDxes =
  isFlagEnabledForUser('USE_NUMBERS_WHEN_APPENDING_REGARD_DXES')

export const getHtmlUseBrLineFormatOnCopy = () =>
  isFlagEnabledForUser('HTML_USE_BR_LINE_FORMAT_ON_COPY')
export const getHtmlWrapWithFontOnCopy = () =>
  isFlagEnabledForUser('HTML_WRAP_WITH_FONT_ON_COPY')
export const htmlFontFaceOnCopy =
  getFlagValue('HTML_FONT_FACE_ON_COPY') ?? 'Arial, sans-serif'
export const htmlFontSizeOnCopy = getFlagValue('HTML_FONT_SIZE_ON_COPY') ?? '3'

export const getHtmlConvertLinesToListOnCopy = () =>
  isFlagEnabledForUser('HTML_CONVERT_LINES_TO_LISTS_ON_COPY')

export const secretCopyOverride = getFlagValue(
  'HTML_SECRET_COPY_OVERRIDE',
) as HtmlString

export const getIsCopyEvidenceEnabled = () =>
  isFlagEnabledForUser('frontend_copy_evidence_enabled')

export const rteSyncInterval = () =>
  getFlagValue('RTE_SYNC_INTERVAL', { defaultValue: undefined }) ??
  getFlagValue('frontend_rte_sync_interval', { defaultValue: 2500 })

export const toolbarSyncInterval = getFlagValue('TOOLBAR_SYNC_INTERVAL', {
  defaultValue: 500,
})

export const typingThrottleTimeout = getFlagValue('TYPING_THROTTLE_TIMEOUT', {
  defaultValue: 100,
})

export const clipboardClearTimeout = getFlagValue('CLIPBOARD_CLEAR_TIMEOUT_MS')

export const getIsNotesAppEnabled = () =>
  !!isFlagEnabledForUser('frontend_notes_app_enabled', {
    enableForAllInternalUsers: true,
  })

// ///////////
// AI Chat //
// ///////////

export const getIsAiChatEnabled = () => !!getFlagValue('AI_CHAT_ENABLED')

export const regardDisabledMessage = getFlagValue('REGARD_DISABLED_MESSAGE')
export const regardDisabledMessageSecondary = getFlagValue(
  'REGARD_DISABLED_MESSAGE_SECONDARY',
)

// For testing only
export const isDraftLoadDisabled = !!getFlagValue('DRAFT_LOAD_DISABLED')
export const isDraftSaveDisabled = !!getFlagValue('DRAFT_SAVE_DISABLED')

// TODO RND-2164 Set the app version of this to do the same thing p
// One Liner
export const getIsOneLinerEnabled = () =>
  isFlagEnabledForUser('frontend_one_liner_enabled')

export const getIsOneLinerCollapsedByDefault = () =>
  isFlagEnabledForUser('REGARD_ONE_LINER_IS_COLLAPSED')

// Scribe
export const getIsScribeEnabled = () => !!getFlagValue('SCRIBE_ENABLED')

// Max-Generated Notes
export const getMaxGeneratedNotesEnabled = () =>
  isFlagEnabledForUser('frontend_max_generated_notes_enabled')

// Discharge Summary
export const getMaxDischargeSummaryEnabled = () =>
  !!getFlagValue('DC_SUMMARY_BTN_ENABLED')

export const getIsSystemBasedNoteButtonEnabled = () =>
  isFlagEnabledForUser('frontend_max_system_based_note_button_enabled')

// Attestation
export const getMaxAttestationEnabled = () =>
  !!getFlagValue('ATTESTATION_BTN_ENABLED')

// Annual Wellness Visit
export const getIsOutpatientEnabledInMoose = () =>
  isFlagEnabledForUser('outpatient_enable_in_moose')

export const getIsOutpatientPrevisitEnabled = () =>
  isFlagEnabledForUser('outpatient_moose_previsit_page_enabled')

// Open Ended Max Chat
export const getIsOpenEndedMaxChatDisabled = () =>
  isFlagEnabledForUser('disable_open_ended_max_chat')

// AI Note Meshing
function getAiNoteEnabledParam() {
  const urlParams = new URLSearchParams(window.location.search)
  const useAiNote = urlParams.get(ENABLE_AI_NOTE_PARAM)
  return useAiNote
}

export const getIsAiNoteMeshingEnabled = () => {
  const aiNoteEnabledParam = getAiNoteEnabledParam()
  if (aiNoteEnabledParam === 'true') {
    return true
  }
  if (aiNoteEnabledParam === 'false') {
    return false
  }

  return !!getFlagValue('REVAMP_ENABLED')
}

// Whether to use hardcoded AI Note or Error page on error
export const getHardcodedNoteEnabled = () => {
  // in a dev environment append &hardcoded-note=true to switch without using feature flags
  if (isDev) {
    const urlParams = new URLSearchParams(window.location.search)
    const useHardcodedNote = urlParams.get(HARDCODED_NOTE_PARAM)
    if (useHardcodedNote === 'true') {
      return true
    }
  }
  return isFlagEnabledForUser('HARDCODED_NOTE_ENABLED')
}

export const getIsVersionToggleEnabled = () =>
  isDev ||
  isSalesDemoMode() ||
  isFlagEnabledForUser('frontend_notes_enable_version_toggle')

export const getHardcodedNoteType = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const noteType = urlParams.get(HARDCODED_NOTE_TYPE_PARAM)
  if (!noteType) {
    return 'default'
  }
  return noteType as HardcodedNoteType
}

export const getAttestationButtonTitle = () =>
  getFlagValue('frontend_max_attestation_button_title')

export const getAttestationButtonChatMessage = () =>
  getFlagValue('frontend_max_attestation_button_chat_message')

export const getIsEvidenceFilteringEnabled = () =>
  isFlagEnabledForUser('frontend_evidence_filtering') && !isSalesDemoMode()

export const getIsConditionRelevanceFilteringEnabled = () =>
  isFlagEnabledForUser('frontend_condition_relevance_filtering')

export const getIsHyperspaceSpeechIntegrationEnabled = () =>
  isFlagEnabledForUser('frontend_hyperspace_speech_integration')

export const getIsTabPanelOpenToDxByDefaultEnabled = () =>
  isFlagEnabledForUser('frontend_tab_panel_open_to_dx_by_default')

// Mirrors the flag automed reads when it picks `initial_base_note_id`.
export const getIsPreliminaryBaseNoteAllowed = () =>
  isFlagEnabledForUser('allow_preliminary_base_notes')

// Editor Api migration flags

export const getIsEditorApiConditionsEnabled = () =>
  isFlagEnabledForUser('frontend_editor_api_conditions_enabled') ||
  isFlagEnabledForUser('frontend_evidence_filtering')

export const getIsEditorLineStatusEnabled = () =>
  isFlagEnabledForUser('frontend_editor_api_line_status_enabled')

export const getIsEditorDesignExplorationEnabled = () =>
  !!getFlagValue('MOOSE_ENABLED') ||
  isFlagEnabledForUser('frontend_editor_design_exploration')

// Max Support Dialog
export const getMaxSupportDialogEnabled = () =>
  isFlagEnabledForUser('frontend_max_support_enabled') &&
  !getIsEditorDesignExplorationEnabled()

export const getIsMooseOldVersionButtonEnabled = () =>
  isFlagEnabledForUser('frontend_moose_old_version_button_enabled')

export const getIsAutoRedirectToMooseDisabled = () =>
  isFlagEnabledForUser(
    'frontend_editor_design_exploration_disable_auto_redirect',
  )

export const getIsMergeLiveDxRegardConditionsEnabled = () =>
  isFlagEnabledForUser('frontend_merge_live_dx_regard_conditions')

export const getIsEditorApiConditionSynonymsEnabled = () =>
  isFlagEnabledForUser('frontend_editor_api_condition_synonyms')

export const getIsEditorConditionsSourceOfTruthEnabled = () =>
  isFlagEnabledForUser('frontend_editor_conditions_source_of_truth')

// Send to Problem List
export const getIsSendToPlEnabled = () =>
  isFlagEnabledForUser('frontend_moose_send_to_problem_list')

// Outpatient Scribe Note Update (DCP scribe notes in MW)
export const getIsScribeNoteUpdateEnabled = () =>
  isFlagEnabledForUser('outpatient_scribe_note_update_in_meshing')

// Admin onboarding flow
export const getIsAdminOnboardingEnabled = () =>
  isFlagEnabledForUser('frontend_admin_onboarding_flow')

// Lets an admin change an existing note type's base type from its settings card.
export const getIsChangeNoteTypeEnabled = () =>
  isFlagEnabledForUser('frontend_admin_change_note_type')

export const getIsUserOnboardingValidationEnabled = () =>
  isFlagEnabledForUser('frontend_user_onboarding_validation')

export const getIsUserOnboardingPrelearningEnabled = () =>
  isFlagEnabledForUser('frontend_user_onboarding_prelearning')

// CareEverywhere / external HIE data in Max's context. meshing gates the feature itself on this
// same flag, per oauth subject and group ID -- both of which this client sends. Disagreement with
// meshing is possible and fails closed.
export const getIsExternalSourceInLlmContextEnabled = () =>
  isFlagEnabledForUser('meshing_show_external_source_in_llm_context')

// Max Chat settings changes. meshing gates the feature itself on this same flag, so this only
// decides whether the changelog announces it.
export const getIsMaxChatSettingsUpdateEnabled = () =>
  isFlagEnabledForUser('max_chat_settings_update_enabled')
